/**
 * Manpower P&L — Vendors API service.
 *
 * Types come from the OpenAPI-generated client (src/api/generated/manpower-vendors.ts,
 * built from the backend's GET /v1/api/manpower-contract/vendors spec), NOT hand-written.
 * The response envelope is always { status, statusCode, message, data } and the JWT +
 * city scoping are handled by the shared axios client (auth interceptor in api.ts).
 */
import { apiService } from './api';
import type { components } from '../api/generated/manpower-vendors';

// Single source of truth for the vendor shapes — straight from the contract.
export type Vendor = components['schemas']['Vendor'];
export type CreateVendorBody = components['schemas']['CreateVendorBody'];
export type UpdateVendorBody = components['schemas']['UpdateVendorBody'];

/** The list response: the standard envelope + a typed `data` array. */
export interface ListVendorsResponse {
	status: boolean;
	statusCode: number;
	message: string;
	data: Vendor[];
}

export interface ListVendorsParams {
	/** Optional city filter. Omit for all cities the caller may see (city scoping is automatic). */
	cityId?: number;
}

export class ManpowerVendorsApiService {
	/**
	 * GET /v1/api/manpower/vendors — list manpower-typed, active vendors.
	 * baseURL already ends in /v1/api, so the relative path is /manpower/vendors.
	 */
	static async listVendors(params?: ListVendorsParams): Promise<ListVendorsResponse> {
		const searchParams = new URLSearchParams();
		if (params?.cityId) {
			searchParams.set('cityId', params.cityId.toString());
		}
		const query = searchParams.toString();
		const response = await apiService.get<ListVendorsResponse>(
			`/manpower/vendors${query ? `?${query}` : ''}`
		);
		// apiService.get already returns response.data from axios, so this IS the envelope.
		return response as unknown as ListVendorsResponse;
	}

	/** GET /vendors/{id} — fetch one vendor. */
	static async getVendor(vendorId: number): Promise<Vendor> {
		const response = await apiService.get<{ data: Vendor }>(`/manpower/vendors/${vendorId}`);
		return (response as unknown as { data: Vendor }).data;
	}

	/** POST /vendors — create a vendor. Returns the new id. */
	static async createVendor(body: CreateVendorBody): Promise<{ id?: number }> {
		const response = await apiService.post<{ data: { id?: number } }>('/manpower/vendors', body);
		return (response as unknown as { data?: { id?: number } }).data ?? {};
	}

	/** PATCH /vendors/{id} — update name / city / status. */
	static async updateVendor(vendorId: number, body: UpdateVendorBody): Promise<void> {
		await apiService.patch(`/manpower/vendors/${vendorId}`, body);
	}

	/** DELETE /vendors/{id} — soft-delete (sets status=inactive). */
	static async deleteVendor(vendorId: number): Promise<void> {
		await apiService.delete(`/manpower/vendors/${vendorId}`);
	}
}
